/**
 * Mola servisi.
 * Personel mesai içinde mola alır. 1.5 saate kadar mesaiye dahil,
 * aşan kısım maaştan düşer (Faz 3'te hesaplanacak).
 *
 * Şu an tüm molalar otomatik onaylı (Sorumlu sistemde yok).
 */

import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import type { FsTimestamp } from '@/types'

const MOLALAR = 'molalar'

/** Toplam mola hakkı dakika cinsinden (1.5 saat) */
export const MOLA_HAKKI_DAKIKA = 90

export type MolaOnayDurumu = 'otomatik' | 'sorumluOnayli' | 'redEdildi'

export interface Mola {
  id: string
  personelId: string
  kayitId: string  // hangi giriş kaydına ait
  baslangic: FsTimestamp
  bitis?: FsTimestamp
  onayDurumu: MolaOnayDurumu
  onaylayanId?: string
}

function yeniId(): string {
  const db = getFirestoreDb()
  return doc(collection(db, MOLALAR)).id
}

/**
 * Personelin aktif (henüz bitmemiş) molasını getir.
 */
export async function aktifMolaGetir(personelId: string): Promise<Mola | null> {
  const db = getFirestoreDb()
  const q = query(collection(db, MOLALAR), where('personelId', '==', personelId))
  const snap = await getDocs(q)
  const aktif = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as Mola)
    .find((m) => !m.bitis)
  return aktif ?? null
}

/**
 * Yeni mola başlat. Aktif mola varsa hata.
 */
export async function molaBaslat(
  personelId: string,
  kayitId: string
): Promise<string> {
  const aktif = await aktifMolaGetir(personelId)
  if (aktif) {
    throw new Error('Zaten aktif bir molandasın.')
  }

  const db = getFirestoreDb()
  const id = yeniId()

  await setDoc(doc(db, MOLALAR, id), {
    personelId,
    kayitId,
    baslangic: nowServer(),
    onayDurumu: 'otomatik' as MolaOnayDurumu,
  })

  return id
}

/**
 * Molayı bitir.
 */
export async function molaBitir(molaId: string): Promise<void> {
  const db = getFirestoreDb()
  await updateDoc(doc(db, MOLALAR, molaId), {
    bitis: nowServer(),
  })
}

/**
 * Personelin bugünkü tüm molalarını getir.
 */
export async function bugunkuMolalar(personelId: string): Promise<Mola[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, MOLALAR), where('personelId', '==', personelId))
  const snap = await getDocs(q)
  const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Mola)

  const bugun = new Date()
  bugun.setHours(0, 0, 0, 0)

  return liste.filter((m) => m.baslangic.toDate() >= bugun)
}

/**
 * Bugünkü tüm molalar (tüm personeller). Yönetici görüntüleme için.
 * Client-side bugün filtresi uygulanır.
 */
export async function tumBugunkuMolalar(): Promise<Mola[]> {
  const db = getFirestoreDb()
  const snap = await getDocs(collection(db, MOLALAR))
  const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Mola)

  const bugun = new Date()
  bugun.setHours(0, 0, 0, 0)

  return liste
    .filter((m) => m.baslangic.toDate() >= bugun)
    .sort((a, b) => b.baslangic.toMillis() - a.baslangic.toMillis())
}

/**
 * Mola listesinden toplam dakika hesapla.
 * Aktif (bitmemiş) mola için şu ana kadar say.
 */
export function molaToplamDakika(molalar: Mola[]): number {
  let toplamMs = 0
  for (const m of molalar) {
    const baslangic = m.baslangic.toMillis()
    const bitis = m.bitis ? m.bitis.toMillis() : Date.now()
    toplamMs += bitis - baslangic
  }
  return Math.floor(toplamMs / 60000)
}

/**
 * Aktif molanın süresi (saniye).
 */
export function aktifMolaSaniye(mola: Mola | null): number {
  if (!mola || mola.bitis) return 0
  return Math.floor((Date.now() - mola.baslangic.toMillis()) / 1000)
}

/**
 * Belirli tarih aralığındaki molalar (başlangıç tarihine göre).
 * BOSS / yönetici düzeltme ekranı için. basISO/bitISO: 'YYYY-MM-DD'.
 */
export async function araliktakiMolalar(
  personelId: string,
  basISO: string,
  bitISO: string
): Promise<Mola[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, MOLALAR), where('personelId', '==', personelId))
  const snap = await getDocs(q)
  const bas = new Date(`${basISO}T00:00:00`).getTime()
  const bit = new Date(`${bitISO}T23:59:59`).getTime()
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as Mola)
    .filter((m) => {
      const t = m.baslangic.toMillis()
      return t >= bas && t <= bit
    })
    .sort((a, b) => b.baslangic.toMillis() - a.baslangic.toMillis())
}

/**
 * Mola başlangıç/bitiş saatini manuel düzelt (BOSS / yönetici).
 * Açık unutulan veya yanlış molayı düzeltmek için. bitiş zorunlu (molayı kapatır).
 */
export async function molaZamanGuncelle(
  molaId: string,
  baslangic: Date,
  bitis: Date
): Promise<void> {
  if (bitis.getTime() <= baslangic.getTime()) {
    throw new Error('Bitiş, başlangıçtan sonra olmalı.')
  }
  const db = getFirestoreDb()
  await updateDoc(doc(db, MOLALAR, molaId), {
    baslangic: Timestamp.fromDate(baslangic),
    bitis: Timestamp.fromDate(bitis),
  })
}

/** Molayı tamamen sil (yanlış açılan mola — kesintiyi geri alır). */
export async function molaSil(molaId: string): Promise<void> {
  const db = getFirestoreDb()
  await deleteDoc(doc(db, MOLALAR, molaId))
}
